import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getItem } from '@/api/items';
import { AiSearchRecommendationContext } from '@/components/items/AiSearchRecommendationContext';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { Card } from '@/components/ui/Card';
import { useFavoriteAction } from '@/hooks/useFavorites';
import { getPublicDisplayTags, getPublicPrimaryRole } from '@/lib/item-public-metadata';
import { itemKeys } from '@/lib/query-keys';
import type { AiSearchPlan, ItemDetail, ItemListItem } from '@/types';

type ItemCardPreviewMode = 'fixed' | 'intrinsic';

interface ItemCardProps {
  item: ItemListItem;
  onClick: () => void;
  previewMode?: ItemCardPreviewMode;
  aiSearchPlan?: AiSearchPlan | null;
  aiSearchQuery?: string;
}

export function ItemCard({
  item,
  onClick,
  previewMode = 'fixed',
  aiSearchPlan,
  aiSearchQuery,
}: ItemCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const favorite = useFavoriteAction(item.id);
  const [isCopyingCode, setIsCopyingCode] = useState(false);
  const isIntrinsicPreview = previewMode === 'intrinsic';
  const hasUsablePreviewDimensions = Boolean(
    item.previewImageWidth && item.previewImageHeight && item.previewImageWidth > 0 && item.previewImageHeight > 0,
  );
  const previewImageWidth = hasUsablePreviewDimensions ? item.previewImageWidth ?? undefined : undefined;
  const previewImageHeight = hasUsablePreviewDimensions ? item.previewImageHeight ?? undefined : undefined;
  const previewFrameClassName = [
    'relative overflow-hidden bg-white',
    !isIntrinsicPreview || !item.previewImageUrl || !hasUsablePreviewDimensions ? 'aspect-[4/3]' : '',
  ].join(' ').trim();
  const favoriteLabel = favorite.isFavorited ? 'Remove from saved items' : 'Save item';
  const savedCountLabel = item.savedCount.toLocaleString();
  const copyLabel = isCopyingCode ? 'Copying…' : 'Copy';
  const primaryRole = getPublicPrimaryRole(item);
  const visibleTags = getPublicDisplayTags(item, { limit: 3 });
  const description = item.excerpt.trim() || 'Explore this curated pattern in detail.';

  const copyCodeToClipboard = async () => {
    if (isCopyingCode) {
      return;
    }

    setIsCopyingCode(true);

    try {
      const cachedItem = queryClient.getQueryData<ItemDetail>(itemKeys.detail(item.id));
      const detailItem = cachedItem ?? await queryClient.fetchQuery({
        queryKey: itemKeys.detail(item.id),
        queryFn: () => getItem(item.id),
      });

      if (!detailItem) {
        throw new Error('Missing item detail.');
      }

      const code = detailItem.code.trim();

      if (!code) {
        toast({
          title: 'No code to copy.',
          description: 'This item does not have code yet.',
          tone: 'info',
        });
        return;
      }

      await navigator.clipboard.writeText(code);
      toast({
        title: 'Code copied.',
        description: 'Copied to your clipboard.',
        tone: 'success',
      });
    } catch {
      toast({
        title: "Couldn't copy.",
        description: 'Please try again in a moment.',
        tone: 'error',
      });
    } finally {
      setIsCopyingCode(false);
    }
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      className="cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
      aria-label={`Open ${item.title}`}
      data-ai-id="item-card"
    >
      <Card className="group transition-none bg-white hover:bg-[#f9f9f9]" data-ai-id="item-card-panel">
        <div className={[previewFrameClassName, 'group/item-card-preview'].join(' ')} data-ai-id="item-card-preview">
          <div
            className="absolute right-3 top-3 z-10 flex flex-col items-center gap-1"
            data-ai-id="item-card-favorite-stack"
          >
            <button
              type="button"
              aria-pressed={favorite.isFavorited}
              aria-label={favoriteLabel}
              disabled={favorite.isLoading}
              onClick={async (event) => {
                event.preventDefault();
                event.stopPropagation();

                try {
                  await favorite.toggleFavoriteOrRedirect();
                } catch {
                  // keep the card interactive even if the favorite request fails
                }
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              className={[
                'inline-flex h-10 w-10 items-center justify-center rounded-full border bg-white/95 text-xl leading-none shadow-sm backdrop-blur-sm',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-white',
                favorite.isFavorited
                  ? 'border-rose-200 bg-rose-50 text-rose-600'
                  : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-white hover:text-slate-700',
                favorite.isLoading ? 'cursor-wait opacity-70' : 'cursor-pointer',
              ].join(' ')}
              data-ai-id="item-card-favorite-button"
            >
              <span aria-hidden="true" data-ai-id="item-card-favorite-button-icon">
                {favorite.isFavorited ? '♥' : '♡'}
              </span>
              <span className="sr-only">{favoriteLabel}</span>
            </button>
            <div
              className="min-w-8 rounded-full bg-white/90 px-2 py-0.5 text-center text-[11px] font-semibold leading-none text-slate-700 shadow-sm backdrop-blur-sm"
              data-ai-id="item-card-favorite-count"
              aria-label={`Saved by ${savedCountLabel} people`}
            >
              {savedCountLabel}
            </div>
          </div>
          <button
            type="button"
            aria-label="Copy code"
            disabled={isCopyingCode}
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await copyCodeToClipboard();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            className={[
              'absolute inset-x-3 bottom-3 z-10 inline-flex h-10 items-center justify-center border border-emerald-700 bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition-all',
              'focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-white',
              'opacity-0 translate-y-2 pointer-events-none',
              'group-hover/item-card-preview:translate-y-0 group-hover/item-card-preview:opacity-100 group-hover/item-card-preview:pointer-events-auto',
              'group-focus-within/item-card-preview:translate-y-0 group-focus-within/item-card-preview:opacity-100 group-focus-within/item-card-preview:pointer-events-auto',
              'hover:bg-emerald-500 focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:pointer-events-auto',
              isCopyingCode ? 'cursor-wait' : 'cursor-pointer',
            ].join(' ')}
            data-ai-id="item-card-copy-button"
          >
            {copyLabel}
          </button>
          {item.previewImageUrl ? (
            isIntrinsicPreview ? (
              hasUsablePreviewDimensions ? (
                <img
                  src={item.previewImageUrl}
                  alt={item.title}
                  width={previewImageWidth}
                  height={previewImageHeight}
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-full"
                  data-ai-id="item-card-preview-image"
                />
              ) : (
                <img
                  src={item.previewImageUrl}
                  alt={item.title}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                  data-ai-id="item-card-preview-image-fallback"
                />
              )
            ) : (
              <img
                src={item.previewImageUrl}
                alt={item.title}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
                data-ai-id="item-card-preview-image"
              />
            )
          ) : (
            <div
              className="flex h-full items-center justify-center text-sm text-slate-400"
              data-ai-id="item-card-preview-empty"
            >
              No preview available
            </div>
          )}
        </div>

        <div className="space-y-3 border-t border-[#eee] px-4 py-4" data-ai-id="item-card-content">
          <div className="flex flex-wrap items-center gap-2" data-ai-id="item-card-badges">
            {primaryRole ? (
              <Badge variant="default" data-ai-id="item-card-primary-role-badge">
                {primaryRole.name}
              </Badge>
            ) : null}
            <Badge variant="outline" data-ai-id="item-card-type-badge">
              {item.type}
            </Badge>
            {item.isFeatured ? (
              <Badge variant="secondary" data-ai-id="item-card-featured-badge">
                Featured
              </Badge>
            ) : null}
          </div>

          <div className="space-y-2" data-ai-id="item-card-copy">
            <h3 className="text-sm font-semibold leading-5 text-slate-900" data-ai-id="item-card-title">
              {item.title}
            </h3>
            <p className="line-clamp-2 text-sm leading-6 text-slate-600" data-ai-id="item-card-excerpt">
              {description}
            </p>
          </div>

          {aiSearchPlan && aiSearchQuery ? (
            <AiSearchRecommendationContext
              query={aiSearchQuery}
              plan={aiSearchPlan}
              item={item}
              variant="compact"
              aiIdPrefix="item-card-ai-recommendation-context"
            />
          ) : null}

          {visibleTags.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-ai-id="item-card-tag-list">
              {visibleTags.map((tag) => (
                <Badge key={tag.slug} variant="outline" data-ai-id="item-card-tag-badge">
                  {tag.name}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
